import { useState } from "react";
import type { MouseEvent } from "react";
import { Box, IconButton, Paper, Popover, Typography } from "@mui/material";
import type { ParkingLot } from "../api/types";
import { getOccupancyColor } from "../hooks/useParkingLotAvailability";
import type { OccupancyColor } from "../hooks/useParkingLotAvailability";

type Props = {
  parkingLots: ParkingLot[];
};

// One-character status shown inside each pin, mirroring the gauge colors.
const PIN_LABELS: Record<OccupancyColor, string> = {
  success: "空",
  warning: "混",
  error: "満",
  primary: "-",
};

export function CampusMap({ parkingLots }: Props) {
  const [anchor, setAnchor] = useState<{ el: HTMLElement; lotId: number } | null>(null);

  const handlePinClick = (event: MouseEvent<HTMLElement>, lotId: number) => {
    setAnchor({ el: event.currentTarget, lotId });
  };

  const selectedLot = anchor ? parkingLots.find((lot) => lot.id === anchor.lotId) : undefined;

  // Position is set from admin-web's map editor (PATCH /parking-lots/{id}),
  // not hardcoded here — a lot with no position yet just has no pin.
  const pinnedLots = parkingLots.filter(
    (lot): lot is ParkingLot & { x_percent: number; y_percent: number } =>
      lot.x_percent != null && lot.y_percent != null,
  );

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        mb: { xs: 3, sm: 4 },
        borderRadius: 3,
        overflow: "hidden",
        border: 1,
        borderColor: "divider",
        lineHeight: 0,
      }}
    >
      <Box component="img" src="/campus-map.png" alt="キャンパスマップ" sx={{ display: "block", width: "100%", height: "auto" }} />

      {pinnedLots.map((lot) => {
        const occupancyColor = getOccupancyColor(lot);
        const color = `${occupancyColor}.main`;
        const label = PIN_LABELS[occupancyColor];
        return (
          <IconButton
            key={lot.id}
            onClick={(e) => handlePinClick(e, lot.id)}
            aria-label={lot.name}
            sx={{
              position: "absolute",
              left: `${lot.x_percent}%`,
              top: `${lot.y_percent}%`,
              transform: "translate(-50%, -100%)",
              width: { xs: 34, sm: 40 },
              height: { xs: 42, sm: 50 },
              p: 0,
              borderRadius: 0,
              "&:hover": { filter: "brightness(0.9)" },
            }}
          >
            <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
              <Box
                sx={{
                  width: { xs: 34, sm: 40 },
                  height: { xs: 34, sm: 40 },
                  borderRadius: "50%",
                  bgcolor: color,
                  border: "2px solid white",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ color: "common.white", fontWeight: 700, fontSize: { xs: 14, sm: 16 }, lineHeight: 1 }}>
                  {label}
                </Typography>
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 0,
                  height: 0,
                  borderLeft: { xs: "6px solid transparent", sm: "7px solid transparent" },
                  borderRight: { xs: "6px solid transparent", sm: "7px solid transparent" },
                  borderTop: { xs: "8px solid", sm: "10px solid" },
                  borderTopColor: color,
                }}
              />
            </Box>
          </IconButton>
        );
      })}

      <Popover
        open={anchor !== null}
        anchorEl={anchor?.el ?? null}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Paper sx={{ p: 2, minWidth: 160 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {selectedLot?.name}
          </Typography>
          <Typography sx={{ mt: 0.5 }}>
            <Typography component="span" variant="h5" sx={{ fontWeight: 700 }}>
              {selectedLot?.current_count}
            </Typography>
            <Typography component="span" variant="body2" color="text.secondary">
              {" "}
              / {selectedLot?.capacity} 台
            </Typography>
          </Typography>
        </Paper>
      </Popover>
    </Box>
  );
}
